import PropTypes from "prop-types";
import { useMemo } from "react";
import { useWatch } from "react-hook-form";
import { z } from "zod";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  BaseForm,
  FormInput,
  FormSelect,
  FormTag,
  FormToggle,
} from "components/BaseForm";
import { EMAIL_URL_LANGS } from "constants/Constants";
import tagConstants from "constants/Tag";
import { startCase } from "helpers/string";
import { useEmailUrlMutations } from "hooks/useEmailUrlsTable";

const MODEL_TYPE = tagConstants.taggableModels.email_url;
const { entity: ENTITY_MODEL, contact: CONTACT_MODEL } =
  tagConstants.taggableModels;

const autoTagsSchema = z.array(z.object({ id: z.number() }).passthrough());

const schema = z.object({
  name: z.string().min(1, "Name is required"),
  url: z
    .string()
    .min(1, "URL is required")
    .regex(
      /^https?:\/\//,
      "Invalid URL format: Valid Example -> https://www.example.com"
    ),
  is_form: z.boolean(),
  entity_auto_tags: autoTagsSchema,
  contact_auto_tags: autoTagsSchema,
  notify_email: z.string(),
  language: z.string().nullable(),
  clicky_id: z.string(),
  clicky_key: z.string(),
  active: z.boolean(),
  tags: z.array(z.any()),
});

const LANGUAGE_OPTIONS = Object.entries(EMAIL_URL_LANGS).map(
  ([key, value]) => ({ key, value, text: startCase(key) })
);

// The fields that configure how a submitted form is processed. FormTag offers
// only enabled, manually applied tags, matching the API's auto tag rule. Auto
// tags are saved as ids, so they carry no metadata.
const FormSettingsFields = () => {
  const isForm = useWatch({ name: "is_form" });

  return (
    <>
      <FormToggle name="is_form" label="Is Form?" />
      {isForm && (
        <>
          <FormTag
            name="entity_auto_tags"
            label="Entity Auto Tags"
            modelType={ENTITY_MODEL}
            withMetadata={false}
          />
          <FormTag
            name="contact_auto_tags"
            label="Contact Auto Tags"
            modelType={CONTACT_MODEL}
            withMetadata={false}
          />
          <FormInput name="notify_email" label="Notify Email" />
          <FormSelect
            name="language"
            label="Language"
            options={LANGUAGE_OPTIONS}
            clearable
          />
        </>
      )}
    </>
  );
};

/**
 * Create and edit form for an Email URL. Without `emailUrl` it creates one.
 */
const EmailUrlForm = ({ emailUrl, onSaved, onCancel }) => {
  const { create, update } = useEmailUrlMutations();

  const defaultValues = useMemo(
    () => ({
      name: emailUrl?.name ?? "",
      url: emailUrl?.url ?? "",
      is_form: emailUrl?.is_form ?? false,
      entity_auto_tags: emailUrl?.entity_auto_tags ?? [],
      contact_auto_tags: emailUrl?.contact_auto_tags ?? [],
      notify_email: emailUrl?.notify_email ?? "",
      language: emailUrl
        ? (emailUrl.language ?? null)
        : EMAIL_URL_LANGS.ENGLISH,
      clicky_id: emailUrl?.clicky_id ?? "",
      clicky_key: emailUrl?.clicky_key ?? "",
      active: emailUrl?.active ?? true,
      tags: emailUrl?.tags ?? [],
    }),
    [emailUrl]
  );

  // The API takes auto tags as ids and returns them as tag records.
  const handleSubmit = async ({
    entity_auto_tags,
    contact_auto_tags,
    ...values
  }) => {
    const data = {
      ...values,
      entity_auto_tag_ids: entity_auto_tags.map(tag => tag.id),
      contact_auto_tag_ids: contact_auto_tags.map(tag => tag.id),
    };
    if (emailUrl) {
      await update.mutateAsync({ id: emailUrl.id, data });
    } else {
      await create.mutateAsync(data);
    }
  };

  return (
    <BaseForm
      schema={schema}
      defaultValues={defaultValues}
      onSubmit={handleSubmit}
      onSuccess={onSaved}
      onCancel={onCancel}
      submitButtonText="Save"
    >
      <Card>
        <CardHeader>
          <CardTitle>Details</CardTitle>
        </CardHeader>
        <CardContent className="tw:flex tw:flex-col tw:gap-5">
          <FormInput name="name" label="Name" required />
          <FormInput
            name="url"
            label="URL"
            required
            placeholder="https://www.example-url.com"
          />
          <FormToggle name="active" label="Active" />
          <FormTag name="tags" modelType={MODEL_TYPE} />
        </CardContent>
      </Card>
      <Card>
        <CardHeader>
          <CardTitle>Form</CardTitle>
        </CardHeader>
        <CardContent className="tw:flex tw:flex-col tw:gap-5">
          <FormSettingsFields />
        </CardContent>
      </Card>
      <Card>
        <CardHeader>
          <CardTitle>Clicky Analytics</CardTitle>
        </CardHeader>
        <CardContent className="tw:flex tw:flex-col tw:gap-5">
          <FormInput name="clicky_id" label="Clicky ID" />
          <FormInput name="clicky_key" label="Clicky Key" />
        </CardContent>
      </Card>
    </BaseForm>
  );
};

const AUTO_TAG_SHAPE = PropTypes.shape({
  id: PropTypes.number.isRequired,
  name: PropTypes.string,
  color: PropTypes.string,
});

EmailUrlForm.propTypes = {
  emailUrl: PropTypes.shape({
    id: PropTypes.number.isRequired,
    name: PropTypes.string,
    url: PropTypes.string,
    is_form: PropTypes.bool,
    entity_auto_tags: PropTypes.arrayOf(AUTO_TAG_SHAPE),
    contact_auto_tags: PropTypes.arrayOf(AUTO_TAG_SHAPE),
    notify_email: PropTypes.string,
    language: PropTypes.string,
    clicky_id: PropTypes.string,
    clicky_key: PropTypes.string,
    active: PropTypes.bool,
    tags: PropTypes.array,
  }),
  onSaved: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
};

export default EmailUrlForm;
